import { deepClone } from "@mixitone/mvc/src/utils/deepClone";
import { SingleEliminationFormat } from "./SingleEliminationFormat";
import { SwissFormat } from "./SwissFormat";
import {
  type EntryDataField,
  type FormatSchema,
  type SchemaSettings,
  type StandingsData,
  TournamentFormat,
} from "./TournamentFormat";
import { type Entry, type Match, type Round, type TournamentState, blankTournamentState } from "./types";

const schema = {
  swissRounds: {
    type: "number",
    description: "Number of Swiss rounds to play before splitting into divisions",
    minimum: 1,
    maximum: 10,
    default: 3,
  },
  divisions: {
    type: "number",
    description: "Number of divisions to split into",
    minimum: 1,
    maximum: 8,
    default: 2,
  },
  pointsForWin: {
    type: "number",
    description: "Points awarded for a win in Swiss phase",
    default: 1,
  },
  pointsForDraw: {
    type: "number",
    description: "Points awarded for a draw in Swiss phase",
    default: 0.5,
  },
  pointsForLoss: {
    type: "number",
    description: "Points awarded for a loss in Swiss phase",
    default: 0,
  },
} satisfies FormatSchema;

/**
 * Swiss with Divisions tournament format
 * This format starts with a Swiss tournament for a specified number of rounds,
 * then splits players into divisions based on their performance,
 * and runs a single elimination tournament within each division.
 */
export class SwissWithDivisionsFormat extends TournamentFormat<typeof schema> {
  getName(): string {
    return "Swiss Monrad with Divisions";
  }

  get description(): string {
    return "Swiss/Monrad format followed by division-based playoffs";
  }

  getEntryLimits(): { min: number; max: number } {
    return {
      min: 4,
      max: 128,
    };
  }

  getSettingsSchema() {
    return schema;
  }

  getEntryDataSchema(): EntryDataField[] {
    return [
      {
        key: "score",
        label: "Score",
        type: "number" as const,
        description: "Total points earned in Swiss phase",
        sortable: true,
      },
      {
        key: "position",
        label: "Position",
        type: "number" as const,
        description: "Final position after Swiss phase",
        sortable: true,
      },
      {
        key: "division",
        label: "Division",
        type: "number" as const,
        description: "Division assignment after Swiss phase",
        sortable: true,
      },
    ];
  }

  getMatchDataSchema(): EntryDataField[] {
    return [
      {
        key: "division",
        label: "Division",
        type: "number" as const,
        description: "Division this match belongs to",
        sortable: true,
      },
    ];
  }

  transform(state?: TournamentState, settings?: SchemaSettings<typeof schema>): TournamentState {
    // If no state provided, return empty state
    if (!state) {
      return blankTournamentState();
    }

    // Get entries from round 0
    const entries = state.entries;
    if (!entries || entries.length === 0) {
      return blankTournamentState();
    }

    // For 1 entry, just return the original state
    if (entries.length === 1) {
      return state;
    }

    // Create a new tournament state
    const newState: TournamentState = deepClone(state);

    // Get settings with defaults
    const swissRounds = settings?.swissRounds ?? 3;
    const numDivisions = settings?.divisions ?? 2;
    const pointsForWin = settings?.pointsForWin ?? schema.pointsForWin.default;
    const pointsForDraw = settings?.pointsForDraw ?? schema.pointsForDraw.default;
    const pointsForLoss = settings?.pointsForLoss ?? schema.pointsForLoss.default;

    // Create instance of the Swiss format
    const swissFormat = new SwissFormat();

    // Generate Swiss rounds
    const swissSettings = {
      numberOfRounds: swissRounds,
      pointsForWin,
      pointsForDraw,
      pointsForLoss,
    };

    const swissState = swissFormat.transform(
      {
        ...newState,
        rounds: newState.rounds.reduce<Round[]>((acc, round) => {
          const matches = round.filter((match) => match[2]?.division === undefined);
          if (matches.length > 0) {
            acc.push(matches);
          }
          return acc;
        }, []),
      },
      swissSettings,
    );
    const divisionRounds = newState.rounds.map((round) =>
      round.reduce<Round>((acc, match) => {
        if (match) {
          acc.push(match);
        }
        return acc;
      }, []),
    );
    newState.rounds = swissState.rounds;
    newState.winners = [];

    const divisionStates: TournamentState[] = Array(numDivisions)
      .fill(null)
      .map(() => blankTournamentState())
      .map((divisionState, index) => {
        divisionState.rounds = divisionRounds.reduce<Round[]>((acc, round) => {
          const divisionRound: Round = round.filter((match) => match[2]?.division === index + 1);
          if (divisionRound.length > 0) {
            acc.push(divisionRound);
          }
          return acc;
        }, []);

        return divisionState;
      });

    // Check if Swiss phase is complete
    const isSwissComplete = (swissState.winners?.length ?? 0) > 0;

    // Update the scores from the swiss state
    newState.entries = newState.entries.map((entry, idx) => [
      entry[0],
      entry[1],
      {
        ...entry[2],
        ...swissState.entries[idx][2],
      },
    ]);

    // Calculate entries per division for bracket generation
    // This is used when Swiss phase is not complete to generate placeholder brackets
    let entriesLeft = entries.length;
    const entriesPerDivision: number[] = [];

    for (let i = 0; i < numDivisions; i++) {
      const divSize = Math.ceil(entriesLeft / (numDivisions - i));
      entriesPerDivision.push(divSize);
      entriesLeft -= divSize;
    }

    // If Swiss phase is complete, generate division brackets
    if (swissState.winners && isSwissComplete) {
      const validEntryIds = new Set(entries.map((e) => e[0]));

      const sortedEntries: Entry[] = swissState.winners
        // Filter out winners who aren't in the original entries list, i.e.
        // withdrawn entries
        .filter(([entryId, _]) => validEntryIds.has(entryId))
        .map(([entryId, _]) => {
          return entries.find((entry) => entry[0] === entryId) ?? [entryId, 0];
        });

      // Add position to each entry based on their ranking
      const entriesWithPosition: Entry[] = sortedEntries.map((entry, index) => {
        const position = index + 1;
        return [
          entry[0],
          entry[1],
          {
            ...entry[2],
            position,
          },
        ] as Entry;
      });

      // Distribute entries to divisions contiguously by final Swiss ranking.
      // Division 1 gets the top ranked entries, division 2 the next block, etc.
      let rankingIndex = 0;
      for (let division = 0; division < numDivisions; division++) {
        const divisionSize = entriesPerDivision[division] ?? 0;
        for (let i = 0; i < divisionSize && rankingIndex < entriesWithPosition.length; i++) {
          const entry = entriesWithPosition[rankingIndex];
          const position = (entry[2]?.position as number) ?? rankingIndex + 1;

          // Add the entry to the division state
          divisionStates[division].entries.push(entry);

          // Update the entry in the main state with division info and position
          const entryId = entry[0];
          const idx = newState.entries.findIndex((e) => e[0] === entryId);
          if (idx !== -1) {
            newState.entries[idx][2] = {
              ...newState.entries[idx][2],
              division: division + 1,
              position,
            };
          }
          rankingIndex++;
        }
      }

      // Adjust seeds for proper bracket pairing in each division
      for (let d = 0; d < numDivisions; d++) {
        divisionStates[d].entries = this.adjustSeedsForStriping(divisionStates[d].entries);
      }
    }

    const singleEliminationFormat = new SingleEliminationFormat();
    const divisionsRoundIndex = newState.rounds.length;

    for (let d = 0; d < divisionStates.length; d++) {
      // Use actual entries in division if Swiss phase is complete, otherwise use calculated value
      const numPlayers =
        isSwissComplete && divisionStates[d].entries.length > 0
          ? divisionStates[d].entries.length
          : entriesPerDivision[d];
      divisionStates[d] = singleEliminationFormat.transform(divisionStates[d], {
        numPlayers,
      });

      const divisionRounds = divisionStates[d].rounds.map((round) =>
        round.map((match) => addMetaToMatch(match, { division: d + 1 })),
      );

      for (let i = 0; i < divisionRounds.length; i++) {
        newState.rounds[divisionsRoundIndex + i] ||= [];
        newState.rounds[divisionsRoundIndex + i].push(...divisionRounds[i]);
      }

      const winners = divisionStates[d].winners ?? [];
      for (const winner of winners) {
        newState.winners.push([winner[0], { ...winner[1], division: d + 1 }]);
      }
    }

    return newState;
  }

  /**
   * Returns the standings data for the tournament
   * This calculates the current standings based on the Swiss phase results
   *
   * @param state - The current tournament state
   * @param settings - Format-specific settings
   * @returns Standings data with a single "All" group showing Swiss phase results
   */
  getStandings(state: TournamentState, settings?: SchemaSettings<typeof schema>): StandingsData {
    // Get settings with defaults
    const pointsForWin = settings?.pointsForWin ?? schema.pointsForWin.default;
    const pointsForDraw = settings?.pointsForDraw ?? schema.pointsForDraw.default;
    const pointsForLoss = settings?.pointsForLoss ?? schema.pointsForLoss.default;

    // Extract only Swiss phase rounds (matches without division metadata)
    const swissRounds: Round[] = state.rounds.reduce<Round[]>((acc, round) => {
      const swissMatches = round.filter((match) => match && match[2]?.division === undefined);
      if (swissMatches.length > 0) {
        acc.push(swissMatches);
      }
      return acc;
    }, []);

    // Create a simplified state with only Swiss rounds
    const swissState: TournamentState = {
      ...state,
      rounds: swissRounds,
    };

    // Use SwissFormat to calculate standings
    const swissFormat = new SwissFormat();
    return swissFormat.getStandings(swissState, {
      pointsForWin,
      pointsForDraw,
      pointsForLoss,
    });
  }

  hasStandings(): boolean {
    return true;
  }

  /**
   * Adjust seeds for entries to create striping pattern in division matches
   * This ensures that when SingleEliminationFormat sorts by seed, it naturally pairs
   * entries from different performance levels using a striping pattern
   *
   * The pattern: first half gets seeds 1..N/2, second half gets seeds N..N/2+1 (reversed)
   * This creates pairing: 1 vs N, 2 vs N-1, etc., which gives us the striping pattern
   */
  private adjustSeedsForStriping(entries: Entry[]): Entry[] {
    if (entries.length === 0) return entries;

    const n = entries.length;
    const half = Math.ceil(n / 2);

    // Apply striping pattern by adjusting seeds:
    // First half: seeds 1, 2, 3, ..., half
    // Second half: seeds n, n-1, n-2, ..., half+1 (reversed)
    return entries.map((entry, index) => {
      let newSeed: number;
      if (index < half) {
        // First half: sequential seeds starting from 1
        newSeed = index + 1;
      } else {
        // Second half: reversed seeds (n, n-1, ..., half+1)
        newSeed = n - (index - half);
      }

      // Return entry with adjusted seed
      return [entry[0], newSeed, entry[2]] as Entry;
    });
  }
}

function addMetaToMatch(match: Match, meta: Record<string, unknown>): Match {
  return [match[0], match[1], { ...match[2], ...meta }];
}
